import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DashboardKpis } from "../../../api/dashboard";
import {
  getCustomerProfitability,
  getProductProfitability,
  type ProfitSummary,
} from "../../../api/profitability";
import { formatMinor } from "../../../lib/money";
import { RoleCode, hasRole, type RoleCodeValue } from "../../../lib/rbac";

const MINI_GREEN = "#38ef7d";
const MINI_RED = "#f45c43";
const MINI_GOLD = "#f2c94c";
const MINI_PURPLE = "#8e2de2";
const MINI_BLUE = "#3498db";
const MINI_TEAL = "#1abc9c";
const DONUT_TRACK = "#d8e8df";

function tooltipMoneyFormatter(value: unknown): string {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return formatMinor(Number.isFinite(n) ? n : 0);
}

interface MiniKpiDonut {
  id: string;
  label: string;
  valueText: string;
  percent: number;
  color: string;
}

interface MiniDashboardPanelProps {
  kpis: DashboardKpis | null;
  isLoading: boolean;
  startDate: string;
  endDate: string;
  canProfitCharts: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
  lastUpdatedAt: Date | null;
}

function truncateLabel(name: string, max = 14): string {
  const s = name.trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function buildMiniKpis(
  kpis: DashboardKpis,
  profitSummary: ProfitSummary | null,
): MiniKpiDonut[] {
  const sales = profitSummary?.revenue_minor ?? kpis.totalSalesMinor;
  const cogs = profitSummary?.cogs_minor ?? kpis.totalExpensesMinor;
  const profit = profitSummary?.gross_profit_minor ?? sales - cogs;
  const grossMarginPct = sales > 0 ? (profit / sales) * 100 : 0;
  const cogsPct = sales > 0 ? (cogs / sales) * 100 : 0;
  const liquidity = kpis.cashBalanceMinor + kpis.bankBalanceMinor;
  const liquidityPct =
    sales > 0 ? Math.min(100, (liquidity / sales) * 100) : Math.min(100, liquidity / 100000);

  return [
    {
      id: "gross_margin_pct",
      label: "هامش ربح المبيعات (%)",
      valueText: `${grossMarginPct.toFixed(1)}%`,
      percent: Math.min(100, Math.max(0, grossMarginPct)),
      color: MINI_GREEN,
    },
    {
      id: "cogs_ratio",
      label: "نسبة التكلفة للمبيعات",
      valueText: `${cogsPct.toFixed(1)}%`,
      percent: Math.min(100, cogsPct),
      color: MINI_RED,
    },
    {
      id: "total_sales",
      label: "إجمالي المبيعات",
      valueText: formatMinor(sales),
      percent: 100,
      color: "#27ae60",
    },
    {
      id: "net_profit_amt",
      label: "صافي ربح المبيعات",
      valueText: formatMinor(profit),
      percent: Math.min(100, Math.max(0, grossMarginPct)),
      color: "#16a085",
    },
    {
      id: "liquidity",
      label: "نقد + بنك",
      valueText: formatMinor(liquidity),
      percent: liquidityPct,
      color: MINI_BLUE,
    },
    {
      id: "receivables",
      label: "ذمم مدينة",
      valueText: formatMinor(kpis.receivablesMinor),
      percent:
        sales > 0
          ? Math.min(100, (kpis.receivablesMinor / sales) * 100)
          : Math.min(100, kpis.receivablesMinor / 100000),
      color: MINI_PURPLE,
    },
    {
      id: "payables",
      label: "ذمم دائنة",
      valueText: formatMinor(kpis.payablesMinor),
      percent:
        sales > 0
          ? Math.min(100, (kpis.payablesMinor / sales) * 100)
          : Math.min(100, kpis.payablesMinor / 100000),
      color: MINI_GOLD,
    },
    {
      id: "pending_wo",
      label: "أوامر تشغيل معلّقة",
      valueText: String(kpis.pendingWorkOrdersCount),
      percent: Math.min(100, kpis.pendingWorkOrdersCount * 12),
      color: "#e67e22",
    },
    {
      id: "active_employees",
      label: "موظفون نشطون",
      valueText: String(kpis.activeEmployeesCount),
      percent: Math.min(100, kpis.activeEmployeesCount * 8),
      color: MINI_TEAL,
    },
    {
      id: "inventory_value",
      label: "قيمة المخزون (تصنيف)",
      valueText: formatMinor(
        kpis.inventoryDistribution.reduce((s, b) => s + b.totalValueMinor, 0),
      ),
      percent: 100,
      color: "#9b59b6",
    },
    {
      id: "expense_total",
      label: "مصروفات الفترة",
      valueText: formatMinor(kpis.totalExpensesMinor),
      percent: Math.min(100, cogsPct),
      color: "#c0392b",
    },
    {
      id: "stock_types",
      label: "أنواع أصناف بالمخزون",
      valueText: String(
        kpis.inventoryDistribution.filter((b) => b.itemCount > 0).length,
      ),
      percent: Math.min(100, kpis.inventoryDistribution.length * 33),
      color: "#95a5a6",
    },
  ];
}

function MiniDonutCard({ kpi }: { kpi: MiniKpiDonut }) {
  const val = Math.max(0, Math.min(100, kpi.percent));
  const data = [
    { name: "val", value: val },
    { name: "rem", value: 100 - val },
  ];

  return (
    <article className="dash-mini-kpi-card">
      <div className="dash-mini-canvas-wrap" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius="74%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              <Cell fill={kpi.color} />
              <Cell fill={DONUT_TRACK} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="dash-mini-kpi-label">{kpi.label}</p>
      <p className="dash-mini-kpi-value" dir="ltr">
        {kpi.valueText}
      </p>
    </article>
  );
}

export function MiniDashboardPanel({
  kpis,
  isLoading,
  startDate,
  endDate,
  canProfitCharts,
  onRefresh,
  isRefreshing,
  lastUpdatedAt,
}: MiniDashboardPanelProps) {
  const { t } = useTranslation();
  const [topItems, setTopItems] = useState<{ name: string; profit: number }[]>([]);
  const [topClients, setTopClients] = useState<{ name: string; profit: number }[]>([]);
  const [profitSummary, setProfitSummary] = useState<ProfitSummary | null>(null);
  const [profitLoading, setProfitLoading] = useState(false);

  useEffect(() => {
    if (!canProfitCharts || !startDate || !endDate) {
      setTopItems([]);
      setTopClients([]);
      setProfitSummary(null);
      return;
    }
    let cancelled = false;
    setProfitLoading(true);
    void (async () => {
      try {
        const [prod, cust] = await Promise.all([
          getProductProfitability({ start: startDate, end: endDate, limit: 8 }),
          getCustomerProfitability({ start: startDate, end: endDate, limit: 6 }),
        ]);
        if (cancelled) return;
        setTopItems(
          (prod.data ?? []).map((r) => ({
            name: r.product_name,
            profit: r.gross_profit_minor,
          })),
        );
        setProfitSummary(prod.summary ?? null);
        setTopClients(
          (cust.data ?? []).map((r) => ({
            name: r.customer_name,
            profit: r.gross_profit_minor,
          })),
        );
      } catch {
        if (!cancelled) {
          setTopItems([]);
          setTopClients([]);
        }
      } finally {
        if (!cancelled) setProfitLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canProfitCharts, startDate, endDate]);

  const salesTrend = useMemo(() => {
    if (!kpis) return [];
    return kpis.monthlySalesTrend.map((p) => ({
      month: p.month,
      totalMinor: p.totalMinor,
      label: truncateLabel(p.month, 8),
    }));
  }, [kpis]);

  const branchFallback = useMemo(() => {
    if (!kpis) return [];
    return kpis.inventoryDistribution
      .filter((b) => b.totalValueMinor > 0)
      .map((b) => ({
        name:
          b.itemType === 0
            ? t("inventory.item_type_storable")
            : b.itemType === 1
              ? t("inventory.item_type_service")
              : t("inventory.item_type_consumable"),
        profit: b.totalValueMinor,
      }));
  }, [kpis, t]);

  const miniKpis = useMemo(
    () => (kpis ? buildMiniKpis(kpis, profitSummary) : []),
    [kpis, profitSummary],
  );

  const chartsBusy = isLoading || profitLoading;

  return (
    <section className="dash-mini-main" aria-label={t("dashboard.section_charts")}>
      <div className="dash-mini-header">
        <span className="dash-mini-title">
          {t("dashboard.mini_live_title", { defaultValue: "Go.Do — التحليل المالي المباشر" })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {lastUpdatedAt ? (
            <span className="text-[10px] text-[#2d6a4f]">
              {t("dashboard.live_updated", {
                time: lastUpdatedAt.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              })}
            </span>
          ) : null}
          <button type="button" className="dash-refresh-btn" onClick={onRefresh} disabled={isRefreshing}>
            {isRefreshing
              ? t("dashboard.live_syncing")
              : t("dashboard.mini_refresh", { defaultValue: "🔄 تحديث فوري" })}
          </button>
        </div>
      </div>

      <div className="dash-top-grid">
        <article className="dash-card">
          <h2 className="dash-card-title">
            {t("dashboard.dash_chart_item", {
              defaultValue: "ربحية حسب الصنف (Top 8)",
            })}
          </h2>
          <div className="dash-chart-box">
            {chartsBusy ? (
              <div className="dash-chart-skeleton" />
            ) : topItems.length > 0 ? (
              <div className="h-full w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topItems.map((r) => ({ ...r, label: truncateLabel(r.name) }))}
                    margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                  >
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#2d6a4f" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: "#64748b" }} width={42} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={tooltipMoneyFormatter}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.name ? String(payload[0].payload.name) : ""
                      }
                    />
                    <Bar dataKey="profit" fill={MINI_GREEN} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : salesTrend.some((r) => r.totalMinor > 0) ? (
              <div className="h-full w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={salesTrend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9 }} width={42} axisLine={false} tickLine={false} />
                    <Tooltip formatter={tooltipMoneyFormatter} />
                    <Bar dataKey="totalMinor" fill={MINI_GREEN} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="dash-chart-empty">{t("dashboard.chart_sales_empty")}</p>
            )}
          </div>
        </article>

        <article className="dash-card">
          <h2 className="dash-card-title">
            {t("dashboard.dash_chart_client", { defaultValue: "ربحية حسب العميل (Top 6)" })}
          </h2>
          <div className="dash-chart-box">
            {chartsBusy ? (
              <div className="dash-chart-skeleton" />
            ) : topClients.length > 0 ? (
              <div className="h-full w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topClients}
                      dataKey="profit"
                      nameKey="name"
                      innerRadius="52%"
                      outerRadius="78%"
                      paddingAngle={2}
                      stroke="#fff"
                      strokeWidth={1}
                    >
                      {topClients.map((_, i) => (
                        <Cell
                          key={i}
                          fill={[MINI_GREEN, MINI_RED, MINI_GOLD, MINI_PURPLE, MINI_BLUE, MINI_TEAL][i % 6]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={tooltipMoneyFormatter} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="dash-chart-empty">{t("dashboard.chart_sales_empty")}</p>
            )}
          </div>
        </article>

        <article className="dash-card">
          <h2 className="dash-card-title">
            {t("dashboard.dash_chart_branch", {
              defaultValue: "توزيع المخزون / الفروع",
            })}
          </h2>
          <div className="dash-chart-box">
            {chartsBusy ? (
              <div className="dash-chart-skeleton" />
            ) : branchFallback.length > 0 ? (
              <div className="h-full w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={branchFallback}
                      dataKey="profit"
                      nameKey="name"
                      innerRadius="52%"
                      outerRadius="78%"
                      paddingAngle={2}
                      stroke="#fff"
                      strokeWidth={1}
                    >
                      {branchFallback.map((_, i) => (
                        <Cell
                          key={i}
                          fill={[MINI_TEAL, MINI_GOLD, MINI_PURPLE, MINI_GREEN][i % 4]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={tooltipMoneyFormatter} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="dash-chart-empty">{t("dashboard.chart_inventory_empty")}</p>
            )}
          </div>
        </article>
      </div>

      <div className="dash-mini-kpi-grid">
        {isLoading
          ? Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="dash-mini-kpi-card animate-pulse bg-white/60" />
            ))
          : miniKpis.map((kpi) => <MiniDonutCard key={kpi.id} kpi={kpi} />)}
      </div>
    </section>
  );
}

export function dashboardCanProfitCharts(roles: RoleCodeValue[]): boolean {
  return hasRole(roles, RoleCode.SUPER_ADMIN);
}
